import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPhone } from 'react-icons/fi';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { useMilestoneWatchlist } from '../../hooks/useMilestones';
import {
    EmptyState,
    SkeletonTableRow,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import { telLink } from '../../utils/format';
import { buildPdfReport } from '../../utils/pdfReport';
import {
    STATUS,
    sortForQueue,
} from '../../utils/milestonesFilter';

/**
 * Reminders → Milestones — clinical-triage queue.
 *
 * Shows DELAYED children only — past the 1-month grace period without a
 * 'yes' recorded, OR explicitly marked 'no' by the PHM. PENDING and DUE
 * rows are off this surface (they belong to the next-clinic logistical
 * view, which lives elsewhere).
 *
 * MUI Table of every delayed child × milestone row, grouped by child
 * (rowSpan on reg / name / phone so a child with many delayed milestones
 * only appears once in those columns), sorted most-severe first. Click
 * any row in a child group to jump to that child's per-child capture
 * page at /milestones/:id.
 *
 * Filter rules live in utils/milestonesFilter.js and run unit-checked
 * by utils/milestonesFilter.test.mjs.
 *
 * Status rule (single source of truth, FE mirror of the BE watchlist):
 *   DELAYED  — past grace, OR selection === 'no'  (the only status on this page)
 */

const statusChipSx = (status, theme) => {
    switch (status) {
        case STATUS.PENDING:
            return {
                bgcolor: theme.palette.warning.light,
                color: theme.palette.warning.contrastText ?? '#7c2d12',
                fontWeight: 600,
            };
        case STATUS.DUE:
            return {
                bgcolor: theme.palette.warning.main,
                color: '#fff',
                fontWeight: 600,
            };
        case STATUS.DELAYED:
            return {
                bgcolor: theme.palette.error.main,
                color: '#fff',
                fontWeight: 700,
            };
        default:
            return {};
    }
};

// Stable empty array — prevents the "new [] on every render" warning
// from exhaustive-deps when `data` is briefly undefined during fetch.
const EMPTY_ITEMS = Object.freeze([]);

const Milestones = ({ onExportChange }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const toast = useToast();
    const { data, loading, error, refetch } = useMilestoneWatchlist();
    const items = data?.items ?? EMPTY_ITEMS;

    // Apply the CDSS filter (utils/milestonesFilter). Result is the
    // sorted DELAYED rows that will feed the grouped table below.
    const filteredRows = useMemo(() => sortForQueue(items), [items]);

    // Group the sorted DELAYED rows by child, preserving the most-severe-
    // first order from sortForQueue. The first row of each group carries
    // the child-info cells (reg + name + phone) with rowSpan so the table
    // doesn't repeat them per milestone.
    const groupedRows = useMemo(() => {
        const map = new Map();
        for (const r of filteredRows) {
            if (!map.has(r.childId)) map.set(r.childId, []);
            map.get(r.childId).push(r);
        }
        return Array.from(map.values()).map((rows) => ({
            childId: rows[0].childId,
            rows,
        }));
    }, [filteredRows]);

    // PDF export — just the flat delayed-triage queue. No status summary
    // and no subtitle line: the triage table is the entire point of this
    // report, and the row count is already implicit in the page count.
    // Same `onExportChange` pattern as UpcomingVaccines / Growth so the
    // parent's Export PDF button stays contextual to the active tab.
    const exportPDF = useCallback(() => {
        const stamp = new Date().toISOString().split('T')[0];
        const statusFor = (r) =>
            r.selection === 'no'
                ? 'DELAYED — Marked NO'
                : `DELAYED — ${Math.abs(r.daysToBand)} day${Math.abs(r.daysToBand) === 1 ? '' : 's'} past grace`;
        buildPdfReport({
            title: 'Milestone Screening Reminders Report',
            filters: [],
            sections: [
                {
                    heading: 'Delayed Triage Queue',
                    head: ['Reg No', 'Child Name', 'Milestone', 'Age Band', 'Status', "Mother's Phone"],
                    body: filteredRows.map((r) => [
                        r.childNo || '-',
                        r.childName || '-',
                        r.milestoneLabel || r.milestoneKey || '-',
                        r.ageBand || '-',
                        statusFor(r),
                        r.contactNumber || '-',
                    ]),
                    accent: [220, 38, 38], // red-600
                },
            ],
            fileName: `Milestone_Screening_Reminders_${stamp}.pdf`,
            toast,
        });
    }, [filteredRows, toast]);

    useEffect(() => {
        onExportChange?.({ exportPDF, canExport: filteredRows.length > 0 });
    }, [onExportChange, exportPDF, filteredRows.length]);

    // Click anywhere on a child group → open that child's per-child
    // milestone capture page. The tel: link inside the phone cell stops
    // propagation so clicking a phone number opens the dialer instead
    // of navigating.
    const openChild = (id) => navigate(`/milestones/${id}`);

    if (loading) {
        return (
            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Registration Number</TableCell>
                                <TableCell>Child Name</TableCell>
                                <TableCell>Milestone</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Mother's Phone Number</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <SkeletonTableRow key={i} cols={5} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        );
    }

    if (error) {
        const message = typeof error === 'string' ? error : error?.message || 'Unknown error';
        return (
            <EmptyState
                illustration="error"
                title="Couldn't load milestone watchlist"
                description={message}
                action={
                    <PageButton type="button" variant="primary" onClick={() => refetch()}>
                        Try again
                    </PageButton>
                }
            />
        );
    }

    return (
        <Stack spacing={3}>
            {/* Triage table. */}
            <Card
                component="section"
                aria-label="Delayed milestone screening list"
                sx={{ overflow: 'hidden' }}
            >
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Registration Number</TableCell>
                                <TableCell>Child Name</TableCell>
                                <TableCell>Milestone</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Mother's Phone Number</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedRows.map((group) =>
                                group.rows.map((r, idx) => {
                                    const isFirst = idx === 0;
                                    return (
                                        <TableRow
                                            key={`${r.childId}-${r.milestoneKey}`}
                                            hover
                                            onClick={() => openChild(r.childId)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            {isFirst && (
                                                <>
                                                    <TableCell
                                                        rowSpan={group.rows.length}
                                                        sx={{
                                                            fontVariantNumeric: 'tabular-nums',
                                                            verticalAlign: 'middle',
                                                        }}
                                                    >
                                                        {r.childNo || '-'}
                                                    </TableCell>
                                                    <TableCell
                                                        rowSpan={group.rows.length}
                                                        sx={{
                                                            fontWeight: 600,
                                                            verticalAlign: 'middle',
                                                        }}
                                                    >
                                                        <Stack spacing={0.25}>
                                                            <span>{r.childName || '-'}</span>
                                                            {r.ageMonths != null && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    {r.ageMonths}{' '}
                                                                    {r.ageMonths === 1 ? 'month' : 'months'} old
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </>
                                            )}
                                            <TableCell>
                                                <Stack spacing={0.25}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        {r.milestoneLabel || r.milestoneKey}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {r.ageBand}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        r.selection === 'no'
                                                            ? 'DELAYED — Marked NO'
                                                            : `DELAYED — ${Math.abs(r.daysToBand)} day${Math.abs(r.daysToBand) === 1 ? '' : 's'} past grace`
                                                    }
                                                    sx={statusChipSx(STATUS.DELAYED, theme)}
                                                />
                                            </TableCell>
                                            {isFirst && (
                                                <TableCell
                                                    rowSpan={group.rows.length}
                                                    sx={{ verticalAlign: 'middle' }}
                                                >
                                                    {r.contactNumber ? (
                                                        <Box
                                                            component="a"
                                                            href={telLink(r.contactNumber)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 0.75,
                                                                color: 'primary.main',
                                                                textDecoration: 'none',
                                                                fontVariantNumeric: 'tabular-nums',
                                                                fontWeight: 500,
                                                                '&:hover': { textDecoration: 'underline' },
                                                            }}
                                                            title="Call caregiver"
                                                        >
                                                            <FiPhone size={14} aria-hidden="true" />
                                                            {r.contactNumber}
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            component="span"
                                                        >
                                                            —
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                }),
                            )}
                            {groupedRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <EmptyState
                                            illustration="calendar"
                                            compact
                                            title="No delayed milestone screenings"
                                            description="No children are currently past the 1-month grace period without a 'yes' recorded, or marked 'no' on a milestone."
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Stack>
    );
};

export default Milestones;
